import React, { useEffect, useState } from "react"
import { IDataSource, IJoinStep, IQuery, IQueryBuilderContext, IStepValidationResponse, StepType } from "@types"
import { findSourceById, getSelectedStepDetailsById, handleStepFieldValueChanges, registerStep, validateFieldConfiguration, validateSource, validateStep, validateStepNameAndId } from "@utils"
import { FormSection } from "@components/FormSection/FormSection"
import { SourceSelectorInput } from "../SourceSelector/SourceSelectorInput"
import { SourceMapper, validateConditions } from "../SourceMapper/SourceMapper"
import { FieldMapper } from "../FieldMapper/FieldMapper"
import { Checkbox, hasValue } from "uxp/components"
import Icon from '@images/join.svg'

export function renderStep(context: IQueryBuilderContext, query: IQuery, onChange: (query: IQuery) => void, selectedStepId: string) {
    const { step, index, previousSteps, selectedSources } = getSelectedStepDetailsById(query, selectedStepId)
    const _step: IJoinStep = Object.assign({}, step as IJoinStep)

    const [errors, setErrors] = useState<Record<string, string>>({})

    useEffect(() => {
        const { isValid, errors } = validateStep(query, step as IJoinStep)
        setErrors(errors)
    }, [step])

    const source1 = findSourceById(query, _step?.source1)
    const source2 = findSourceById(query, _step?.source2)

    async function onValueChange(field: string, value: any, source?: boolean) {
        const _query = await handleStepFieldValueChanges(context, query, selectedStepId, field, value, source)
        onChange(_query)
    }

    return <React.Fragment>
        <FormSection
            label='Source 01'
            error={errors?.source1}
        >
            <SourceSelectorInput
                source={source1}
                onChange={(newSource) => { onValueChange('source1', newSource, true) }}
                availableSources={[...previousSteps, ...selectedSources]}
                query={query}
            />
        </FormSection>

        <FormSection
            label='Source 02'
            error={errors?.source2}
        >
            <SourceSelectorInput
                source={source2}
                onChange={(newSource) => { onValueChange('source2', newSource, true) }}
                availableSources={[...previousSteps, ...selectedSources]}
                query={query}
            />
        </FormSection>

        <FormSection
            label='Match Records by'
            error={errors?.on}
        >
            <SourceMapper
                conditions={_step?.on || []}
                source1={source1 as IDataSource}
                source2={source2 as IDataSource}
                onChange={conditions => onValueChange('on', conditions)}
            />
        </FormSection>
        <FormSection
            label='Fields to include'
            error={errors?.source2Fields}
        >
            <FieldMapper
                fields={_step?.source2Fields || []}
                source={source2 as IDataSource}
                onChange={fields => onValueChange('source2Fields', fields)}
            />
        </FormSection>
        <FormSection
            label='Optional'
            error=''
        >
            <Checkbox checked={_step.optional} onChange={s => onValueChange('optional', s)} />
        </FormSection>
    </React.Fragment>
}

function validate(query: IQuery, step: IJoinStep): IStepValidationResponse {
    const errors: Record<string, string> = validateStepNameAndId(step);
    // validate source
    const source1 = validateSource(query, step, 'source1')
    if (!source1.isValid) errors.source1 = source1.error

    const source2 = validateSource(query, step, 'source2')
    if (!source2.isValid) errors.source2 = source2.error

    const conditions = validateConditions(step?.on)
    if (!conditions.isValid) errors.on = conditions.error
    else {
        const { uniqueAttributesSource1, uniqueAttributesSource2 } = conditions

        const fields1 = validateFieldConfiguration(query, step, uniqueAttributesSource1, 'source1')
        if (!fields1.isValid) errors.on = fields1.error
        else {
            const fields2 = validateFieldConfiguration(query, step, uniqueAttributesSource2, 'source2')
            if (!fields2.isValid) errors.on = fields2.error
        }
    }

    if (!step?.source2Fields || step?.source2Fields?.length == 0) errors.source2Fields = 'Select atleast one field from the second source'
    else {
        const fields3 = validateFieldConfiguration(query, step, step?.source2Fields?.map(f => (f.field)), 'source2')
        if (!fields3.isValid) errors.source2Fields = fields3.error

        // check if selected field/alias already exists in source 1
        const source1Obj = findSourceById(query, step.source1) as IDataSource | undefined
        if (source1Obj) {
            // Collect all field names and aliases from source1
            const source1Fields = new Set(source1Obj?.schema?.map(field => field?.name));

            // Check for conflicts in source2Fields
            for (const fieldObj of step.source2Fields) {
                const fieldName = hasValue(fieldObj.newField) ? fieldObj.newField : fieldObj.field;
                if (source1Fields.has(fieldName)) {
                    errors.source2Fields = `Field or alias "${fieldName}" already exists in Source 1. Please choose a unique name.`
                    break;
                }
            }
        }
    }

    return { isValid: Object.keys(errors).length === 0, errors };
}

registerStep({
    type: StepType.JOIN,
    name: "Join",
    description: "Join another data source",
    icon: Icon,
    iconStyles: { backgroundSize: '110%' },
    render: renderStep,
    validate: validate,
    onSelectStep: (basicProps, previousSource) => {
        return { ...basicProps, source1: previousSource, source2: '', on: [], optional: false, source2Fields: [], } as IJoinStep
    }
})